import { cn } from "@babackapp/customer-ui/lib/utils";
import {
  AnimatedButtonContainer,
  AnimatedButtonRow,
  Button,
} from "@babackapp/customer-ui/components/button";
import {
  Header,
  HeaderDescription,
  HeaderTitle,
} from "@babackapp/customer-ui/components/header";
import {
  Option,
  OptionAction,
  OptionDescription,
  OptionIcon,
  OptionsList,
  OptionTitle,
} from "@babackapp/customer-ui/components/option";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import { EnterIcon } from "@radix-ui/react-icons";
import { ArrowLeft, ChevronRight, RefreshCcw, WalletIcon } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useLineItemResolutionForm } from "../../hooks/use-line-item-resolution-form";
import {
  getAutoCommittableLeaf,
  type SingleFamilyLeaf,
} from "@babackapp/return-widget-components/lib/resolutions.lib";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import { UserPortalCallout } from "@babackapp/return-widget-components/components/user-portal-callout";
import { useUpdateLineItem } from "@babackapp/return-widget-components/hooks/use-update-line-item";
import { useWidgetParams } from "../../hooks/use-widget-params";
import { useResolutionSuggestions, type SuggestionItem } from "../../hooks/use-resolution-suggestions";
import { LineItemBar } from "./line-item-bar";
import { PromotedLeafTile } from "./promoted-leaf-tile";
import { SuggestionGrid } from "./suggestion-grid";

export function Resolution() {
  const {
    form,
    onSubmit,
    commitLeaf,
    rawResolutions,
    availableResolutions,
    isRetractationItem,
    formattedShippingCost,
    userPortalTexts,
    leafOptions,
  } = useLineItemResolutionForm();
  const { lineItemId = "" } = useParams<{ lineItemId: string }>();
  const { orderId } = useWidgetParams();
  const updateLineItem = useUpdateLineItem(orderId);
  const navigate = useNavigate();
  const suggestions = useResolutionSuggestions();
  const t = useCustomText();
  const [selectedLeaf, setSelectedLeaf] =
    useState<NonNullable<SingleFamilyLeaf> | null>(null);

  const canExchange = availableResolutions.includes("exchange");
  // Size chips and "Other sizes…" both end on a variant exchange: the sizing
  // card needs the variant exchange itself, not just the exchange family (an
  // exchange-credit-only family has no size to swap to).
  const canPickOtherSize = rawResolutions.includes("exchange");
  const showSuggestions =
    !!suggestions &&
    suggestions.items.length > 0 &&
    (suggestions.type === "sizing" ? canPickOtherSize : canExchange);

  const RESOLUTION_CONFIG: Record<
    "refund" | "exchange",
    { icon: React.ReactNode; title: string; description: string }
  > = {
    refund: {
      icon: <WalletIcon />,
      title: t("resolution.refund.title"),
      description: isRetractationItem
        ? t("returnMethod.retractation.costInfo", { amount: formattedShippingCost })
        : t("resolution.refund.description"),
    },
    exchange: {
      icon: <RefreshCcw />,
      title: t("resolution.exchange.title"),
      description: t("resolution.exchange.description"),
    },
  };

  // Hook will navigate away when only one resolution — render nothing in the meantime
  if (availableResolutions.length <= 1) return null;

  const currentVariant = suggestions?.currentVariantTitle;
  const suggestionTitle =
    suggestions?.type === "sizing"
      ? currentVariant
        ? t("suggestions.sizing.titleWithVariant", { variant: currentVariant })
        : t("suggestions.sizing.title")
      : t("suggestions.products.title");

  function handleSuggestionSelect(item: SuggestionItem) {
    if (item.variantId) {
      updateLineItem(lineItemId, {
        resolution: "exchange",
        exchangeVariantId: item.variantId,
      });
      navigate("../../../summary");
      return;
    }
    navigate(`../product-selection?productId=${item.id}`, {
      state: { imageUrl: item.imageUrl, label: item.label },
    });
  }

  // Straight to the size picker, the state "Change variant" leaves on
  // resolution-options, even when exchange credit is offered next to it. The
  // nearest suggested size (in stock, first) is preselected, and replaces a
  // size picked earlier from a chip: the customer asked for other sizes.
  function handleOtherSizes() {
    updateLineItem(lineItemId, {
      resolution: "exchange",
      resolutionOption: null,
      exchangeVariantId: suggestions?.items[0]?.variantId ?? null,
    });
    navigate("../resolution-options");
  }

  function handleSelectResolution(value: "refund" | "exchange") {
    form.setValue("resolution", value);
    onSubmit();
  }

  return (
    // The sizing card adds a block to the page: tighter gaps keep the back
    // button in view without scrolling in the default embed height.
    <WidgetPage className={showSuggestions ? "gap-4 compact:gap-3" : undefined}>
      <Header>
        <HeaderTitle>{t("resolution.title")}</HeaderTitle>
        <HeaderDescription>{t("resolution.description")}</HeaderDescription>
      </Header>
      <UserPortalCallout texts={userPortalTexts} />
      <LineItemBar variant="inline" />

      {/* The card reads as the first choice of the list: kept close to it.
          The inline bar pulls the next block up by 24px (sized for the page's
          40px gap): with the tighter 16px gap, `mt-5` puts the card 12px under it,
          like an option. */}
      <div
        className={cn(
          "flex flex-col gap-3",
          showSuggestions && "mt-5 compact:mt-0",
        )}
      >
        <AnimatePresence mode="wait">
          {showSuggestions && suggestions && (
            <motion.div
              key="suggestion-grid"
              initial={{ opacity: 0, filter: "blur(8px)" }}
              animate={{ opacity: 1, filter: "blur(0px)", transition: { duration: 0.4, ease: "easeOut" } }}
              exit={{ opacity: 0, filter: "blur(8px)", transition: { duration: 0.25, ease: "easeIn" } }}
            >
              <SuggestionGrid
                title={suggestionTitle}
                items={suggestions.items}
                onSelect={handleSuggestionSelect}
                moreAction={
                  suggestions.type === "sizing"
                    ? {
                        label: t("suggestions.sizing.otherSizes"),
                        onClick: handleOtherSizes,
                      }
                    : undefined
                }
              />
            </motion.div>
          )}
        </AnimatePresence>

        <OptionsList>
          {availableResolutions.map((value) => {
            const autoLeaf = getAutoCommittableLeaf(
              value,
              rawResolutions,
              leafOptions,
            );
            if (autoLeaf) {
              return (
                <PromotedLeafTile
                  key={value}
                  leaf={autoLeaf}
                  selected={selectedLeaf?.resolution === autoLeaf.resolution}
                  onSelect={() => setSelectedLeaf(autoLeaf)}
                />
              );
            }
            const config = RESOLUTION_CONFIG[value];
            return (
              <button
                key={value}
                type="button"
                className="w-full text-left cursor-pointer"
                onClick={() => handleSelectResolution(value)}
              >
                <Option>
                  <OptionIcon>{config.icon}</OptionIcon>
                  <OptionTitle>{config.title}</OptionTitle>
                  <OptionDescription>{config.description}</OptionDescription>
                  <OptionAction>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </OptionAction>
                </Option>
              </button>
            );
          })}
        </OptionsList>
      </div>

      <AnimatedButtonRow variants={{}}>
        <AnimatedButtonContainer
          layoutId="line-item-back-button"
          disableVariants={{ exit: true }}
        >
          <Link to="../reason">
            <Button variant="secondary" type="button">
              <ArrowLeft />
              {t("common.back")}
            </Button>
          </Link>
        </AnimatedButtonContainer>
        {selectedLeaf && (
          <AnimatedButtonContainer
            layoutId="line-item-confirm-button"
            disableVariants={{ enter: true, exit: true }}
          >
            <Button type="button" onClick={() => commitLeaf(selectedLeaf)}>
              <EnterIcon />
              {t("common.confirm")}
            </Button>
          </AnimatedButtonContainer>
        )}
      </AnimatedButtonRow>
    </WidgetPage>
  );
}
